import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

// --- Styles ---
const Container = styled.div`
  background: #f0f0f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  font-family: 'Courier New', Courier, monospace;
  line-height: 1.5;
`;

const Message = styled.p`
  margin: 4px 0;
`;

const blink = keyframes`
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
`;

const Cursor = styled.span`
  display: inline-block;
  width: 8px;
  background: #333;
  margin-left: 2px;
  animation: ${blink} 1s step-start infinite;
`;

// --- Props ---
interface ThinkingProps {
  steps: string[];
  onComplete: () => void;
  delayPerToken?: number;    // ms per token
  pauseBetweenSteps?: number; // extra ms after each step
}

// --- Component ---
const Thinking: React.FC<ThinkingProps> = ({ steps, onComplete, delayPerToken = 10, pauseBetweenSteps = 200 }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [displayedTokens, setDisplayedTokens] = useState<string[]>([]);

  useEffect(() => {
    if (currentStep >= steps.length) {
      onComplete();
      return;
    }

    const tokens = steps[currentStep].split(' ');
    let idx = 0;
    setDisplayedTokens([]);

    const tokenInterval = setInterval(() => {
      setDisplayedTokens(tokens.slice(0, idx + 1));
      idx++;
      if (idx === tokens.length) {
        clearInterval(tokenInterval);
      }
    }, delayPerToken);

    const stepTimeout = setTimeout(() => {
      setCurrentStep(prev => prev + 1);
    }, tokens.length * delayPerToken + pauseBetweenSteps);

    return () => {
      clearInterval(tokenInterval);
      clearTimeout(stepTimeout);
    };
  }, [currentStep, steps, delayPerToken, pauseBetweenSteps, onComplete]);

  return (
    <Container>
      {steps.slice(0, currentStep).map((msg, i) => (
        <Message key={i}>{msg}</Message>
      ))}

      {currentStep < steps.length && (
        <Message>
          {displayedTokens.join(' ')}<Cursor />
        </Message>
      )}
    </Container>
  );
};

export default Thinking;
